import React, { useEffect, useState, useMemo } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    TextField,
    Typography,
    Box,
    IconButton,
    makeStyles,
    useTheme,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import {
    useApplicationList,
    useGrantApplicationToCompany,
    useSetCompanyApplicationPrice,
    useRevokeApplicationFromCompany,
    AdminCompany,
} from './hooks/useAdminApplicationAccess'
import { CompanyApplication } from '../../../models/Company'

function formatPrice(price: string): string {
    const n = parseFloat(price)
    if (Number.isNaN(n)) return price
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(n)
}

const useStyles = makeStyles((theme) => ({
    addFormBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        width: '100%',
        boxSizing: 'border-box',
    },
    addFormRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'flex-end',
        gap: theme.spacing(2),
        width: '100%',
    },
    grantSelect: {
        flex: '1 1 0',
        minWidth: 0,
    },
    grantPriceInput: {
        flex: '1 1 0',
        minWidth: 0,
    },
    grantAddButton: {
        flexShrink: 0,
    },
    listItem: {
        alignItems: 'center',
    },
    listItemPrimary: {
        flex: 1,
        minWidth: 0,
    },
    priceInput: {
        width: 100,
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        '& .MuiInputBase-input': {
            padding: theme.spacing(0.75, 1),
            fontSize: '0.875rem',
        },
    },
    addApplicationSection: {
        marginTop: theme.spacing(2),
    },
}))

interface AdminManageCompanyApplicationsModalProps {
    open: boolean
    company: AdminCompany
    onClose: () => void
}

export const AdminManageCompanyApplicationsModal: React.FC<AdminManageCompanyApplicationsModalProps> = ({
    open,
    company,
    onClose,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const { list: allApplications, fetch: fetchApps } = useApplicationList()
    const { grant } = useGrantApplicationToCompany()
    const { setPrice } = useSetCompanyApplicationPrice()
    const { revoke } = useRevokeApplicationFromCompany()

    /** Snapshot when modal opened (for diff on Save). */
    const [initialApplications, setInitialApplications] = useState<
        CompanyApplication[]
    >([])
    /** Current list shown in UI; all edits (price, add, revoke) are local until Save. */
    const [localApplications, setLocalApplications] = useState<
        CompanyApplication[]
    >([])

    const [showAddForm, setShowAddForm] = useState(false)
    const [addAppId, setAddAppId] = useState<number | ''>('')
    const [addCustomPrice, setAddCustomPrice] = useState('')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (open) {
            const apps = company.applications ?? []
            setInitialApplications(apps)
            setLocalApplications([...apps])
            fetchApps()
            setShowAddForm(false)
            setAddAppId('')
            setAddCustomPrice('')
        }
    }, [open, company, fetchApps])

    const grantedIds = useMemo(
        () => new Set(localApplications.map((a) => a.id)),
        [localApplications],
    )
    const availableToGrant = allApplications.filter(
        (a) => !grantedIds.has(a.id),
    )

    const hasChanges = useMemo(() => {
        const initialIds = new Set(initialApplications.map((a) => a.id))
        const localIds = new Set(localApplications.map((a) => a.id))
        const hasRevokes = initialApplications.some(
            (app) => !localIds.has(app.id),
        )
        const hasGrants = localApplications.some(
            (app) => !initialIds.has(app.id),
        )
        const hasPriceChanges = localApplications.some((local) => {
            const initial = initialApplications.find((a) => a.id === local.id)
            if (!initial) return false
            return (initial.custom_price ?? '') !== (local.custom_price ?? '')
        })
        return hasRevokes || hasGrants || hasPriceChanges
    }, [initialApplications, localApplications])

    const handlePriceChange = (appId: number, value: string) => {
        const trimmed = value.trim()
        setLocalApplications((prev) =>
            prev.map((a) => {
                if (a.id !== appId) return a
                const useDefault =
                    trimmed === '' || trimmed === (a.default_price ?? '')
                const custom_price = useDefault ? null : trimmed
                const effective_price = custom_price ?? a.default_price ?? '0'
                return { ...a, custom_price, effective_price }
            }),
        )
    }

    const handleAddToLocal = () => {
        if (addAppId === '' || typeof addAppId !== 'number') return
        const app = allApplications.find((a) => a.id === addAppId)
        if (!app) return
        const customPrice = addCustomPrice.trim() || null
        const newApp: CompanyApplication = {
            ...app,
            custom_price: customPrice,
            effective_price: customPrice ?? app.default_price ?? '0',
        }
        setLocalApplications((prev) => [...prev, newApp])
        setShowAddForm(false)
        setAddAppId('')
        setAddCustomPrice('')
    }

    const handleRevokeLocal = (appId: number) => {
        setLocalApplications((prev) => prev.filter((a) => a.id !== appId))
    }

    const handleSave = async () => {
        if (!hasChanges) return
        setSaving(true)
        try {
            const initialIds = new Set(initialApplications.map((a) => a.id))
            const localIds = new Set(localApplications.map((a) => a.id))

            // Revokes: in initial but not in local
            const toRevoke = initialApplications.filter(
                (app) => !localIds.has(app.id),
            )
            for (const app of toRevoke) {
                await revoke(company.id, app.id)
            }

            // Grants: in local but not in initial
            const toGrant = localApplications.filter(
                (app) => !initialIds.has(app.id),
            )
            for (const app of toGrant) {
                await grant(company.id, app.id, app.custom_price ?? undefined)
            }

            // Price changes: in both but custom_price changed
            for (const local of localApplications) {
                const initial = initialApplications.find(
                    (a) => a.id === local.id,
                )
                if (!initial) continue
                const a = initial.custom_price ?? null
                const b = local.custom_price ?? null
                const same =
                    (a == null && b == null) ||
                    (a !== null && b !== null && a === b)
                if (!same) {
                    await setPrice(company.id, local.id, b)
                }
            }

            onClose()
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Manage applications – {company.name}</DialogTitle>
            <DialogContent>
                <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                >
                    Granted applications
                </Typography>
                <List dense disablePadding>
                    {localApplications.map((app) => (
                        <ListItem
                            key={app.id}
                            divider
                            className={classes.listItem}
                        >
                            <ListItemText
                                primary={app.name}
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    style: { fontWeight: 500 },
                                }}
                                className={classes.listItemPrimary}
                            />
                            <ListItemSecondaryAction
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    right: 8,
                                }}
                            >
                                {app.custom_price != null && (
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        color="secondary"
                                        style={{ marginRight: 4 }}
                                    >
                                        (custom)
                                    </Typography>
                                )}
                                <TextField
                                    size="small"
                                    placeholder="Use default"
                                    value={
                                        app.custom_price ??
                                        app.default_price ??
                                        ''
                                    }
                                    onChange={(e) =>
                                        handlePriceChange(
                                            app.id,
                                            e.target.value,
                                        )
                                    }
                                    type="text"
                                    inputProps={{
                                        inputMode: 'decimal',
                                        'aria-label': `Price for ${app.name}`,
                                    }}
                                    variant="outlined"
                                    className={classes.priceInput}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => handleRevokeLocal(app.id)}
                                    title="Remove (save to apply)"
                                    style={{ marginLeft: 4 }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>

                <div className={classes.addApplicationSection}>
                    {!showAddForm ? (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setShowAddForm(true)}
                            disabled={availableToGrant.length === 0}
                        >
                            Add application
                        </Button>
                    ) : (
                        <Box
                            className={classes.addFormBox}
                            style={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 8,
                            }}
                        >
                            <Box className={classes.addFormRow}>
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.grantSelect}
                                    required
                                >
                                    <InputLabel>Application *</InputLabel>
                                    <Select
                                        value={addAppId}
                                        onChange={(e) =>
                                            setAddAppId(
                                                e.target.value as number | '',
                                            )
                                        }
                                        label="Application *"
                                    >
                                        {availableToGrant.map((app) => (
                                            <MenuItem
                                                key={app.id}
                                                value={app.id}
                                            >
                                                {app.name} (
                                                {formatPrice(app.default_price)}
                                                )
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    label="Custom price"
                                    placeholder="e.g. 49.00"
                                    value={addCustomPrice}
                                    onChange={(e) =>
                                        setAddCustomPrice(e.target.value)
                                    }
                                    type="text"
                                    inputProps={{ inputMode: 'decimal' }}
                                    variant="outlined"
                                    className={classes.grantPriceInput}
                                    fullWidth
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={handleAddToLocal}
                                    disabled={addAppId === ''}
                                    className={classes.grantAddButton}
                                >
                                    Add
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setShowAddForm(false)
                                        setAddAppId('')
                                        setAddCustomPrice('')
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSave}
                    disabled={!hasChanges || saving}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
